import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { queryKeys } from '~/api/queryKeys';
import { CachedImage } from '~/components/common/cached-image/cached-image';
import { CopyIconButton } from '~/components/common/copy-icon-button';
import { EnrichedAttributeRow } from '~/components/common/enriched-attribute-row';
import { EnrichmentScoreSection } from '~/components/common/enrichment-score-section';
import { RefreshIconButton } from '~/components/common/refresh-icon-button';
import { Badge } from '~/components/ui/badge';
import { Dialog, DialogContent, DialogTitle } from '~/components/ui/dialog';
import { Skeleton } from '~/components/ui/skeleton';
import { sortByImpact } from '~/constants/impact';
import { scoreBadge } from '~/features/catalog-operations/components/product-score';
import { ProductTranslationsSection } from '~/features/catalog-operations/components/translations/product-translations-section';
import {
  useBatchHeader,
  useProductAttributeEdits,
  useProductDetail,
  useProductEnrichment,
} from '~/features/catalog-operations/hooks/use-batch-detail';
import { useDescriptionDescriptorViews } from '~/features/catalog-operations/hooks/use-description-descriptor-views';
import { useProductCategoryDisplay } from '~/features/catalog-operations/hooks/use-product-category-display';
import { useProductOnDemandScore } from '~/features/catalog-operations/hooks/use-product-on-demand-score';
import { applyCurrentValues } from '~/features/catalog-operations/mappers/catalog-operations.mapper';
import type { Product } from '~/features/catalog-operations/types/catalog-operations.types';
import { indexAttributeEdits } from '~/features/catalog-operations/utils/attribute-edits';
import { isMetaHiddenAttribute } from '~/features/catalog-operations/utils/meta-hidden-attributes';
import {
  usesNeedsAttentionDetailRoute,
  type ProductLinkContext,
} from '~/features/catalog-operations/utils/product-detail-link';
import { useCanChooseRetailerPrompt } from '~/hooks/useCanChooseRetailerPrompt';
import type { ProductOptimization } from '~/types/enrichment';
import { cn } from '~/utils/cn';
import { formatDateTime, getTimeZoneAbbrev } from '~/utils/date';

interface Props {
  /** Row the user opened this from. Renders immediately so the dialog has content
   *  before either request resolves — the list already knows most of this. */
  product: Product | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Drops the "Open full detail" link. Set by hosts outside catalog-operations,
   *  where that route is either wrong or unreachable — the content-enrichment
   *  funnel opens detail in its own modal and must not navigate the operator
   *  out of the wizard. */
  hideDetailLink?: boolean;
  /** Referrer context for the "Open full detail" link. `needs-attention` points it
   *  at the cross-batch detail URL, since those products carry no batch. */
  productLinkContext?: ProductLinkContext | undefined;
}

/** One label/value line in the facts column. */
function Fact({
  label,
  value,
  hint,
  mono,
  copy,
}: {
  label: string;
  value: string | null | undefined;
  /** Optional muted, smaller suffix rendered after the value (e.g. a timezone). */
  hint?: string | undefined;
  mono?: boolean;
  copy?: boolean;
}) {
  if (!value) return null;
  const testId = `products-quick-view-fact-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div
      data-testid={testId}
      className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-3 py-1.5"
    >
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="flex min-w-0 items-center gap-1.5">
        <span
          data-testid={`${testId}-value`}
          className={cn('truncate text-sm font-semibold', mono && 'font-mono text-xs')}
          title={value}
        >
          {value}
          {hint && <span className="text-muted-foreground text-xs font-normal"> {hint}</span>}
        </span>
        {copy && (
          <CopyIconButton
            text={value}
            label={`Copy ${label}`}
            copyMessage={`Copied ${label}`}
            testId={`${testId}-copy-button`}
          />
        )}
      </span>
    </div>
  );
}

/** Audience as one phrase, matching the tile. */
function audienceOf(product: Product): string | null {
  const gender = product.gender?.toLowerCase();
  const genderLabel =
    gender === 'female'
      ? "Women's"
      : gender === 'male'
        ? "Men's"
        : gender === 'unisex'
          ? 'Unisex'
          : null;
  const age = product.ageGroup?.toLowerCase();
  const ageLabel = age && age !== 'adult' ? age.charAt(0).toUpperCase() + age.slice(1) : null;
  const joined = [genderLabel, ageLabel].filter(Boolean).join(' · ');
  return joined === '' ? null : joined;
}

/**
 * Quick view: the substance of the product detail page in a dialog, so a
 * reviewer can check a product and carry on down the list instead of navigating
 * away and back. Row data paints instantly; the enrichment half streams in.
 * Read-only by design — editing stays on the full page, where there's room to
 * see what an edit is replacing.
 */
export function ProductQuickViewDialog({
  product,
  open,
  onOpenChange,
  hideDetailLink = false,
  productLinkContext,
}: Props) {
  const { t } = useTranslation('catalog-operations');
  const queryClient = useQueryClient();
  const enabled = open && product !== null;
  // Resolve a numeric GPC category id to its readable name (dash if unresolvable).
  const category = useProductCategoryDisplay(product?.category);
  // The list row doesn't carry the connector, so infer it the same way the detail
  // page does — from the feed source once the overview resolves.
  const { data: detail, isLoading: isDetailLoading } = useProductDetail(
    product?.batchId ?? '',
    product?.id ?? '',
    enabled,
  );
  const connectorCode = detail?.feedSource?.toLowerCase().includes('meta') ? 'META' : undefined;
  const canSeeRetailerPrompt = useCanChooseRetailerPrompt();
  const { data: enrichment, isLoading: isEnrichmentLoading } = useProductEnrichment(
    product?.id ?? '',
    false,
    enabled && !!detail && detail.feedSource !== null,
    connectorCode,
  );
  // Fetched here as well as on the detail page, deliberately. The "Edited" marker
  // is a claim in both directions: without this call every row would read as
  // un-edited, which is a wrong answer rather than a missing one.
  const { data: attributeEdits } = useProductAttributeEdits(
    product?.id ?? '',
    enabled && !!detail && detail.feedSource !== null,
    connectorCode,
  );
  // On-demand scoring — the score path wants the batch's workflow slug, which the
  // list row doesn't carry, so read it off the header (keyed on the batch uuid the
  // row does carry). Inert until the operator triggers it.
  const { data: batchHeader } = useBatchHeader(product?.batchId ?? '', { enabled });
  // Description / Search Descriptors / Phrase Descriptions toggle on the
  // description row — same views as the full detail page. The row's `batchId`
  // pins the read to this batch's producer.
  const { descriptorViewsFor } = useDescriptionDescriptorViews({
    productId: product?.id ?? '',
    // Batchless (needs-attention) rows carry "" — leave those unpinned.
    enrichmentJobId: (product?.batchId ?? '') || undefined,
    enabled: enabled && !!detail && detail.feedSource !== null,
    searchDescriptorsDisabled: batchHeader?.searchDescriptorsEnabled === false,
  });
  // Enriched products only (the `>cap` tail has optimizations/enriched-at but no
  // batch score), and only once the header's workflow id has resolved.
  const canScore =
    !!batchHeader?.workflowId &&
    (!!enrichment?.enrichedAt || (enrichment?.optimizations?.length ?? 0) > 0);
  const onDemandScore = useProductOnDemandScore({
    workflowId: batchHeader?.workflowId ?? '',
    productId: product?.id ?? '',
    connectorCode,
    // Read back an existing on-demand score when the batch left none.
    autoFetch: canScore && !enrichment?.enrichmentScore && !isEnrichmentLoading,
  });

  // META_ADS runs don't optimize the retailer-feed fields (Retailer Id, Image
  // Link, Price, Sale Price), so hide them here too — same rule as the full
  // product detail page.
  const isMetaAds = batchHeader?.useCase === 'META_ADS';
  // Severity order, same as the full detail page — the enrichment mapper returns
  // these alphabetically, which buried the Critical rows among the Mediums.
  const optimizations: ProductOptimization[] = sortByImpact(
    enrichment?.optimizations && detail?.rawFields
      ? applyCurrentValues(enrichment.optimizations, detail.rawFields)
      : (enrichment?.optimizations ?? []),
  ).filter((opt) => !(isMetaAds && isMetaHiddenAttribute(opt)));

  const editProvenance = indexAttributeEdits(attributeEdits ?? [], enrichment?.workflowId);

  // Via `scoreBadge` rather than the raw field, so this badge and the list it was
  // opened from always agree on whether a product has a score worth showing.
  const scoreBadgeValue = product ? scoreBadge(product)?.value : undefined;

  // Same 0–10 vs 0–100 normalisation the detail page applies.
  const scoreBaseFactor = Math.max(
    enrichment?.enrichmentScore?.before ?? 0,
    enrichment?.enrichmentScore?.after ?? 0,
  );
  const scoreBase = scoreBaseFactor > 10 ? 10 : 1;

  // On-demand score (if generated this session) supersedes the batch score; both
  // feed the meter on the 0–10 scale — see the detail page for the rationale.
  const onDemandPre = onDemandScore.result?.pre?.overallScore;
  const onDemandPost = onDemandScore.result?.post?.overallScore;
  const hasOnDemandPair = onDemandPre != null && onDemandPost != null;
  const scoreBefore = hasOnDemandPair
    ? onDemandPre
    : enrichment?.enrichmentScore
      ? enrichment.enrichmentScore.before / scoreBase
      : undefined;
  const scoreAfter = hasOnDemandPair
    ? onDemandPost
    : enrichment?.enrichmentScore
      ? enrichment.enrichmentScore.after / scoreBase
      : undefined;
  const scorePhase = onDemandScore.isChecking
    ? 'loading'
    : onDemandScore.status === 'FAILED'
      ? 'failed'
      : onDemandScore.status === 'PENDING'
        ? 'pending'
        : 'idle';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="products-quick-view-dialog"
        className="flex h-[88vh] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl"
      >
        {/* `pr-12` keeps the header clear of Dialog's own close button. */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b px-5 py-4 pr-12">
          <div className="min-w-0">
            <DialogTitle
              data-testid="products-quick-view-title"
              className="truncate text-base font-semibold"
            >
              {product?.title ?? 'Product'}
            </DialogTitle>
            <p
              data-testid="products-quick-view-source-id"
              className="text-muted-foreground truncate font-mono text-xs"
            >
              {product?.sourceProductId}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* Refetches the catalog-operations queries this dialog reads
                (detail, enrichment, edits) — mirrors the detail page's refresh. */}
            <RefreshIconButton
              testId="products-quick-view-refresh-button"
              label={t('detailHeader.refresh')}
              onRefresh={() =>
                queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all })
              }
            />
            {product &&
              !hideDetailLink &&
              // Batchless (cross-batch needs-attention) products take the dedicated
              // detail URL — the batch route would emit `/catalog-operations//…`.
              (usesNeedsAttentionDetailRoute(product, productLinkContext) ? (
                <Link
                  data-testid="products-quick-view-open-detail-link"
                  to="/catalog-operations/products-need-attention/$productId"
                  params={{ productId: product.id }}
                  className="text-primary flex shrink-0 items-center gap-1.5 text-xs font-semibold hover:underline"
                >
                  Open full detail
                  <ExternalLink className="size-3.5" aria-hidden="true" />
                </Link>
              ) : (
                <Link
                  data-testid="products-quick-view-open-detail-link"
                  to="/catalog-operations/$batchId/products/$productId"
                  params={{ batchId: product.batchId, productId: product.id }}
                  className="text-primary flex shrink-0 items-center gap-1.5 text-xs font-semibold hover:underline"
                >
                  Open full detail
                  <ExternalLink className="size-3.5" aria-hidden="true" />
                </Link>
              ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* Identity — painted from the row, so there's no spinner here. */}
          <div className="grid grid-cols-1 gap-5 border-b p-5 md:grid-cols-[13rem_minmax(0,1fr)]">
            <div className="relative aspect-3/4 w-full overflow-hidden rounded-xl bg-neutral-100">
              <CachedImage src={product?.imageUrl ?? null} alt="" className="h-full w-full" />
              {scoreBadgeValue != null && (
                <span
                  data-testid="products-quick-view-score-badge"
                  className="absolute top-2 left-2 rounded-full bg-white/95 px-1.5 py-0.5 text-[11px] font-bold tabular-nums shadow-sm"
                >
                  {Math.round(scoreBadgeValue)}
                  <span className="text-muted-foreground text-[9px] font-semibold">/100</span>
                </span>
              )}
            </div>

            <div className="min-w-0 divide-y">
              <Fact label="Brand" value={product?.brand} />
              <Fact label="Audience" value={product ? audienceOf(product) : null} />
              <Fact
                label="Category"
                value={category.isResolving ? '…' : category.display || null}
              />
              <Fact label="Colour" value={product?.color} />
              <Fact
                label="Sizes"
                value={product?.sizes && product.sizes.length > 0 ? product.sizes.join(', ') : null}
              />
              <Fact label="Material" value={product?.material} />
              <Fact label="External Id" value={product?.sku} mono copy />
              <Fact label="Lily ID" value={detail?.lilyId} mono copy />
              <Fact
                label="Enriched"
                value={enrichment?.enrichedAt ? formatDateTime(enrichment.enrichedAt) : undefined}
                hint={
                  enrichment?.enrichedAt
                    ? `(${getTimeZoneAbbrev(enrichment.enrichedAt)})`
                    : undefined
                }
              />
              {/* Which retailer prompt rendered — internal users only, retailer runs only.
                  A fallback is the retailer shipping no prompt for this vertical. */}
              {canSeeRetailerPrompt && enrichment?.retailerPrompt && (
                <Fact
                  label="Prompt"
                  value={
                    enrichment.retailerPrompt.fellBack
                      ? `Shared ${enrichment.retailerPrompt.bucket} prompt`
                      : `${enrichment.retailerPrompt.retailer} · ${enrichment.retailerPrompt.bucket}`
                  }
                  hint={
                    enrichment.retailerPrompt.fellBack
                      ? `(${enrichment.retailerPrompt.retailer} has no ${enrichment.retailerPrompt.bucket} prompt)`
                      : `(${enrichment.retailerPrompt.resolvedPromptPath.split('/').pop() ?? ''})`
                  }
                />
              )}

              {(product?.trends.length ?? 0) > 0 && (
                <div
                  data-testid="products-quick-view-trends"
                  className="flex flex-wrap items-center gap-1.5 py-2"
                >
                  {product?.trends.map((trend) => (
                    <Badge
                      key={trend}
                      data-testid="products-quick-view-trend-badge"
                      variant="outline"
                      className="rounded-md border-purple-200 bg-purple-50 px-1.5 py-0.5 text-[11px] font-medium text-purple-700"
                    >
                      {trend}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Enrichment score — batch score, an on-demand score, or a Generate
              control; hidden entirely for an unscored, unscorable product. */}
          {isEnrichmentLoading ? (
            <div data-testid="products-quick-view-score-loading" className="border-b p-5">
              <Skeleton className="mb-3 h-4 w-32 rounded" />
              <Skeleton className="h-20 w-full max-w-2xl rounded-xl" />
            </div>
          ) : (
            <EnrichmentScoreSection
              beforeScore={scoreBefore}
              afterScore={scoreAfter}
              canScore={canScore}
              isBusy={onDemandScore.isBusy}
              phase={scorePhase}
              onScore={onDemandScore.trigger}
              containerClassName="border-b p-5"
              meterWrapperClassName="max-w-2xl"
              meterClassName="bg-transparent p-0"
            />
          )}

          {/* Enriched attributes — the reason to open a product at all. */}
          <div data-testid="products-quick-view-attributes">
            <h3 className="text-muted-foreground px-5 pt-4 pb-2 text-[11px] font-semibold tracking-wide uppercase">
              Enriched attributes
            </h3>
            {isEnrichmentLoading || isDetailLoading ? (
              <div
                data-testid="products-quick-view-attributes-loading"
                className="flex flex-col gap-2 px-5 pb-5"
              >
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            ) : optimizations.length === 0 ? (
              <p
                data-testid="products-quick-view-attributes-empty"
                className="text-muted-foreground px-5 pb-6 text-sm"
              >
                No enriched attributes for this product.
              </p>
            ) : (
              <div data-testid="products-quick-view-attributes-list" className="divide-y border-t">
                {optimizations.map((opt, i) => (
                  // `field` isn't unique across optimizations — index tie-breaks.
                  <EnrichedAttributeRow
                    key={`${opt.field}-${String(i)}`}
                    opt={opt}
                    provenance={editProvenance.get(opt.field)}
                    descriptorViews={descriptorViewsFor(opt)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Translated locales — internal users only (self-gated); nothing renders until the
              product has at least one. Any id of the run works; the row's own is the exact scope. */}
          <ProductTranslationsSection
            workflowId={enrichment?.workflowId ?? batchHeader?.workflowId ?? ''}
            productId={product?.id ?? ''}
            enabled={enabled && !isMetaAds && !!(enrichment?.workflowId ?? batchHeader?.workflowId)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
